import { useMemo } from 'react';
import { gql, useQuery as useApolloQuery } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import {
  fetchWineRatings,
  fetchWineScores,
  fetchAiExplainer,
  fetchTransactionHistory,
  fetchLatestCriticScores,
  fetchProducerNotes,
} from '../services/fetchWineDetail';
import {
  AiSnippet,
  CwRatings,
  DrinkingWindow,
  FaqItem,
  MarketMetrics,
  PriceAndPerformance,
  ProducerProfile,
  SiblingOffer,
} from '../__mocks__/offerDetail/types';

const HALF_HOUR = 30 * 60 * 1000;

// ────────────────────────────────────────────────────────────────────────────
// GraphQL Queries
// ────────────────────────────────────────────────────────────────────────────

const RELATED_VINTAGES_QUERY = gql`
  query RelatedVintages($assetId: Int!, $page: Int, $pageSize: Int) {
    relatedVintages(assetId: $assetId, page: $page, pageSize: $pageSize) {
      results {
        assetId
        lwin18
        vintage
        caseSize
        marketValuePerBottle
        lastTradedValue
        lastTraded
        normalizedCriticScore
        vinousScore
        waScore
      }
      size
    }
  }
`;

const HISTORICAL_MARKET_VALUE_QUERY = gql`
  query HistoricalMarketValue($assetId: Int!, $dateFrom: String, $dateTo: String) {
    historicalMarketValue(assetId: $assetId, dateFrom: $dateFrom, dateTo: $dateTo) {
      assetId
      lwin18
      liveX
      wineSearcher
    }
  }
`;

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface OfferDetail {
  ratings: CwRatings;
  tastingNotes: string | null;
  drinkingWindow: DrinkingWindow | null;
  priceAndPerformance: PriceAndPerformance;
  siblings: SiblingOffer[];
  producer: ProducerProfile | null;
  aiSnippet: AiSnippet | null;
  faqs: FaqItem[];
  marketMetrics: MarketMetrics | null;
}

interface RelatedVintageResult {
  assetId?: number;
  lwin18?: string;
  vintage?: number;
  caseSize?: string;
  marketValuePerBottle?: number;
  lastTradedValue?: number;
  lastTraded?: string;
  normalizedCriticScore?: number;
}

interface RelatedVintagesQueryResult {
  relatedVintages?: {
    results?: RelatedVintageResult[];
    size?: number;
  };
}

interface HistoricalMarketValueResult {
  assetId: number;
  lwin18: string;
  liveX?: unknown;
  wineSearcher?: unknown;
}

interface HistoricalMarketValueQueryResult {
  historicalMarketValue?: HistoricalMarketValueResult[];
}

interface HistoricalDataPoint {
  date: string;
  price: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Helper functions
// ────────────────────────────────────────────────────────────────────────────

function toYear(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function parseHistoricalData(json: unknown): HistoricalDataPoint[] {
  if (!json || typeof json !== 'object') return [];
  try {
    // LiveX and WineSearcher data comes as JSON with date/price pairs
    const data = json as Record<string, number>;
    return Object.entries(data)
      .map(([date, price]) => ({ date, price }))
      .sort((a, b) => a.date.localeCompare(b.date));
  } catch {
    return [];
  }
}

function mapSiblings(results: RelatedVintageResult[] | null | undefined, currentLwin18: string): SiblingOffer[] {
  if (!results) return [];
  return results
    .filter((r) => r.lwin18 && r.lwin18 !== currentLwin18)
    .slice(0, 5)
    .map((r) => ({
      id: r.assetId?.toString() ?? r.lwin18 ?? '',
      name: '',
      vintage: r.vintage ?? 0,
      region: '',
      unitSize: r.caseSize ?? '',
      priceGbp: r.marketValuePerBottle ?? r.lastTradedValue ?? 0,
    }));
}

function buildPriceAndPerformance(historicalData: HistoricalMarketValueResult | null): PriceAndPerformance {
  const liveXData = parseHistoricalData(historicalData?.liveX);
  const wsData = parseHistoricalData(historicalData?.wineSearcher);

  // Prefer LiveX data, fall back to WineSearcher
  const priceHistory = liveXData.length > 0 ? liveXData : wsData;

  return {
    history: priceHistory.map((p) => ({
      date: p.date,
      offerPrice: p.price,
      marketPrice: p.price,
    })),
    criticEvents: [],
    trades: [],
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Main Hook
// ────────────────────────────────────────────────────────────────────────────

interface UseOfferDetailParams {
  lwin18: string | null | undefined;
  assetId: number | null | undefined;
  wineName?: string;
  producerName?: string;
}

export function useOfferDetail({ lwin18, assetId, wineName, producerName }: UseOfferDetailParams): {
  data: OfferDetail | null;
  isLoading: boolean;
} {
  const lwin11 = lwin18 ? lwin18.substring(0, 11) : undefined;
  const enabled = Boolean(lwin11);
  const assetIdEnabled = Boolean(assetId && assetId > 0);
  const producerEnabled = Boolean(producerName);

  // ─── REST API: Wine Ratings ───────────────────────────────────────────────
  const ratingsQuery = useQuery({
    queryKey: ['wineRatings', lwin11],
    queryFn: () => fetchWineRatings([lwin11 as string]),
    enabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── REST API: Wine Scores (tasting notes, drinking window) ───────────────
  const scoresQuery = useQuery({
    queryKey: ['wineScores', lwin11],
    queryFn: () => fetchWineScores(lwin11 as string),
    enabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── REST API: AI Explainer ───────────────────────────────────────────────
  const aiExplainerQuery = useQuery({
    queryKey: ['aiExplainer', lwin11],
    queryFn: () => fetchAiExplainer(lwin11 as string),
    enabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── REST API: Transaction History ────────────────────────────────────────
  const transactionHistoryQuery = useQuery({
    queryKey: ['transactionHistory', lwin11],
    queryFn: () => fetchTransactionHistory(lwin11 as string, { limit: 10 }),
    enabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── REST API: Latest Critic Scores ───────────────────────────────────────
  const criticScoresQuery = useQuery({
    queryKey: ['latestCriticScores', lwin11],
    queryFn: () => fetchLatestCriticScores(lwin11 as string),
    enabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── REST API: Producer Notes ─────────────────────────────────────────────
  const producerNotesQuery = useQuery({
    queryKey: ['producerNotes', producerName],
    queryFn: () => fetchProducerNotes(producerName as string),
    enabled: producerEnabled,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  // ─── GraphQL: Related Vintages (siblings) ─────────────────────────────────
  const relatedVintagesQuery = useApolloQuery<RelatedVintagesQueryResult>(RELATED_VINTAGES_QUERY, {
    variables: { assetId, page: 0, pageSize: 10 },
    skip: !assetIdEnabled,
    fetchPolicy: 'cache-first',
  });

  // ─── GraphQL: Historical Market Value (price history) ─────────────────────
  const historicalQuery = useApolloQuery<HistoricalMarketValueQueryResult>(HISTORICAL_MARKET_VALUE_QUERY, {
    variables: {
      assetId,
      dateFrom: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
    },
    skip: !assetIdEnabled,
    fetchPolicy: 'cache-first',
  });

  // ─── Loading state ────────────────────────────────────────────────────────
  const isLoading =
    ratingsQuery.isLoading ||
    scoresQuery.isLoading ||
    relatedVintagesQuery.loading ||
    historicalQuery.loading ||
    aiExplainerQuery.isLoading ||
    transactionHistoryQuery.isLoading ||
    criticScoresQuery.isLoading ||
    producerNotesQuery.isLoading;

  // ─── Build result ─────────────────────────────────────────────────────────
  const data = useMemo<OfferDetail | null>(() => {
    const ratingRecord = ratingsQuery.data?.[0];
    const scoreRecord = scoresQuery.data;
    const aiExplainerRecord = aiExplainerQuery.data;
    const transactionHistoryData = transactionHistoryQuery.data;
    const criticScoresRecord = criticScoresQuery.data;
    const producerNotesRecord = producerNotesQuery.data;

    // Ratings from cultwineratings API
    const ratings: CwRatings = {
      vintage: ratingRecord?.vintageScore ?? 0,
      critic: ratingRecord?.normalizedCriticScore ?? ratingRecord?.combinedCriticScore ?? 0,
      fairValue: ratingRecord?.fairValueScore ?? 0,
      liquidity: ratingRecord?.liquidityScore ?? 0,
      performance: ratingRecord?.performanceScore ?? 0,
      grade: ratingRecord?.grade ?? 0,
    };

    // Tasting notes from latest_critic_scores or wine_scores API
    const criticWaNote = criticScoresRecord?.waTastingNote ?? null;
    const criticVNote = criticScoresRecord?.vTastingNote ?? null;
    const waNote = scoreRecord?.waTastingNote ?? null;
    const vNote = scoreRecord?.vTastingNote ?? null;
    const tastingNotes = criticWaNote ?? criticVNote ?? waNote ?? vNote ?? null;

    // Drinking window from latest_critic_scores or wine_scores API
    const criticWaFrom = toYear(criticScoresRecord?.waDrinkYearMin);
    const criticWaTo = toYear(criticScoresRecord?.waDrinkYearMax);
    const waFrom = toYear(scoreRecord?.waDrinkYearMin);
    const waTo = toYear(scoreRecord?.waDrinkYearMax);
    const vFrom = toYear(scoreRecord?.vDrinkFrom);
    const vTo = toYear(scoreRecord?.vDrinkTo);
    const drinkFrom = criticWaFrom ?? waFrom ?? vFrom ?? null;
    const drinkBy = criticWaTo ?? waTo ?? vTo ?? null;
    const peak = drinkFrom != null && drinkBy != null ? Math.round((drinkFrom + drinkBy) / 2) : null;

    const drinkingWindow: DrinkingWindow | null =
      drinkFrom != null && drinkBy != null && peak != null ? { drinkFrom, drinkBy, peak } : null;

    // Siblings from GraphQL relatedVintages
    const relatedResults = relatedVintagesQuery.data?.relatedVintages?.results;
    const siblings = mapSiblings(relatedResults, lwin18 ?? '');
    // Enrich siblings with wine name if available
    const enrichedSiblings = siblings.map((s) => ({ ...s, name: wineName ?? s.name }));

    // Price & Performance from GraphQL historicalMarketValue + transaction_history API
    const historicalData = historicalQuery.data?.historicalMarketValue?.[0] ?? null;
    const basePriceAndPerformance = buildPriceAndPerformance(historicalData);

    // Add trades from transaction_history API
    const trades =
      transactionHistoryData?.results?.map((trade) => ({
        date: trade.dealDate,
        unitSize: trade.unitSize,
        price: trade.price,
      })) ?? [];

    // Add critic events from latest_critic_scores API
    const criticEvents = criticScoresRecord?.waScore
      ? [
          {
            date: criticScoresRecord.waScoreDate
              ? new Date(criticScoresRecord.waScoreDate).toISOString().slice(0, 7)
              : '',
            score: criticScoresRecord.waScore,
            criticName: 'Wine Advocate',
            note: criticScoresRecord.waCriticFullname ? `Review by ${criticScoresRecord.waCriticFullname}` : undefined,
          },
        ]
      : [];

    const priceAndPerformance: PriceAndPerformance = {
      ...basePriceAndPerformance,
      trades,
      criticEvents,
    };

    // AI Snippet from ai_explainer API
    const aiSnippet: AiSnippet | null = aiExplainerRecord?.explainer
      ? {
          prose: aiExplainerRecord.explainer,
          attribution: 'Cult Wines Advisory',
        }
      : null;

    // Producer from cultwineratings + producer_notes API
    const producer: ProducerProfile | null =
      ratingRecord || producerNotesRecord
        ? {
            name: ratingRecord?.wineName ?? '',
            region: ratingRecord?.regionName ?? ratingRecord?.region ?? '',
            appellation: ratingRecord?.subRegion,
            philosophy: producerNotesRecord?.producerNote ?? '',
            recentVintages: [],
          }
        : null;

    // FAQs - static for now (could be fetched from an API in the future)
    const faqs: FaqItem[] = [
      {
        question: 'How is the wine stored?',
        answer:
          "All units are held in bond at Cult Wines' insured London City Bond facility. Provenance is verified end-to-end and full chain of custody is recorded.",
      },
      {
        question: 'When can I sell?',
        answer:
          "Liquidity is available on the Trade tab once your unit settles. Cult Wines' market-making facility provides indicative bids on most blue-chip vintages.",
      },
      {
        question: 'What does "Drinking window" mean?',
        answer:
          'A guidance range across which the wine is expected to drink at or near peak. Outside the window the wine may still be enjoyable but is less likely to be optimal.',
      },
    ];

    // Market Metrics from cultwineratings API
    const marketMetrics: MarketMetrics | null = ratingRecord
      ? {
          winesearcherMarketPrice: ratingRecord.winesearcherMarketPrice ?? null,
          livExMarketPrice: ratingRecord.livExMarketPrice ?? null,
          fairValueLx: ratingRecord.fvLx ?? null,
          fairValueWs: ratingRecord.fvWs ?? null,
          valuePct: ratingRecord.valuePct ?? null,
          perf6m: ratingRecord.perf6m ?? null,
          perf1y: ratingRecord.perf1y ?? null,
          perf3y: ratingRecord.perf3y ?? null,
          perf5y: ratingRecord.perf5y ?? null,
          lxPrice6m: ratingRecord.lxPrice6m ?? null,
          lxPrice3y: ratingRecord.lxPrice3y ?? null,
          lxPrice5y: ratingRecord.lxPrice5y ?? null,
          wsPrice6m: ratingRecord.wsPrice6m ?? null,
          wsPrice3y: ratingRecord.wsPrice3y ?? null,
          wsPrice5y: ratingRecord.wsPrice5y ?? null,
        }
      : null;

    return {
      ratings,
      tastingNotes,
      drinkingWindow,
      priceAndPerformance,
      siblings: enrichedSiblings,
      producer,
      aiSnippet,
      faqs,
      marketMetrics,
    };
  }, [
    ratingsQuery.data,
    scoresQuery.data,
    relatedVintagesQuery.data,
    historicalQuery.data,
    aiExplainerQuery.data,
    transactionHistoryQuery.data,
    criticScoresQuery.data,
    producerNotesQuery.data,
    lwin18,
    wineName,
  ]);

  return { data, isLoading };
}
